import React from 'react';
import classnames from 'classnames';
import { gikClassPrefix } from '@gik/core/constants';
import { UISize, UIInputVariant } from '../types';
import SvgIcon from '../SvgIcon';
import noop from '@gik/core/utils/noop';

type NativeInputType =
  | 'hidden'
  | 'text'
  | 'password'
  | 'number'
  | 'email'
  | 'tel'
  | 'color'
  | 'date'
  | 'datetime-local'
  | 'month'
  | 'search'
  | 'url'
  | 'range'
  | 'time'
  | 'week';

// note: '| string' is added at the end to support react-final-form input types
type InputType = NativeInputType | 'numeric' | string;

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange'> {
  /**
   * style variant to use
   */
  variant?: UIInputVariant;

  /**
   * Size the component on a fixed scale
   */
  size?: UISize;

  /**
   * force display of focus state
   */
  focus?: boolean;

  /**
   * Auto focus the input field when the component is mounted
   */
  autoFocus?: boolean;

  /**
   * select the contents of the input when focused
   */
  selectOnFocus?: boolean;

  /**
   * display component in a disabled state
   */
  disabled?: boolean;

  /**
   * value of the input field
   */
  value?: string;

  /**
   * type of the input field
   */
  type?: InputType;

  /**
   * Append creates append blocks that are colored.
   */
  append?: React.ReactNode;

  /**
   * classNames to add to the append elements
   */
  appendClasses?: string;

  /**
   * Prepend creates prepend blocks that are colored.
   */
  prepend?: React.ReactNode;

  /**
   * classNames to add to the prepend elements
   */
  prependClasses?: string;

  /**
   * tabIndex that is passed to the main DOM element.
   * Set this to -1 to exclude this element from the index
   */
  tabIndex?: number;

  /**
   * A function that can be passed to render out a custom input instead of the default one
   */
  // allow use of all here since the customInput could be any component
  // eslint-disable-next-line
  customInput?: (customProps: any) => React.ReactElement;

  /**
   * Limit the value to a maximum number of characters.
   */
  maxLength?: number;

  maxLengthDisplay?: boolean;

  /**
   * classNames for the character limit element
   */
  maxLengthClassName?: string;

  /**
   * if set to higher than 0 the component will indicator itself in a
   * warning state when the input length is nearing the maxLength.
   *
   * note: only works in combination with maxLength
   */
  maxLengthWarningSensitivity?: number;

  /**
   * center the max-length indicator
   */
  maxLengthCentered?: boolean;

  /**
   * display the component in an error state
   */
  hasError?: boolean;

  /**
   * display the component in a warning state
   */
  hasWarning?: boolean;

  /**
   * display the component in a success state
   */
  hasSuccess?: boolean;

  clearable?: boolean;

  collapse?: boolean;
  active?: boolean;

  pill?: boolean;
  squared?: boolean;

  /**
   * Called when the value changes.
   * This differs from the standard onChange callback for form elements.
   * It will not fire when the component is in a disabled state and it
   * will return the value as a string (instead of an entire onChange Event)
   */
  onValueChange?: (value: string) => void;

  /**
   * Called when the input's value changes.
   */
  onChange?: (event: React.FormEvent<HTMLInputElement>) => void;
  onClear?: () => void;

  // FIXME: shouldn't this be in the HTMLElement props?
  name?: string;
}

// block name for this component
export const inputBlockName = `${gikClassPrefix}-input`;

/**
 * GIK Input component
 */
function Input(
  {
    value,
    className,
    variant = 'default',
    size = 'base',
    tabIndex,
    disabled,
    focus,
    autoFocus,
    selectOnFocus,
    append,
    appendClasses = '',
    prepend,
    prependClasses = '',
    customInput,
    maxLength,
    maxLengthDisplay = maxLength > 0,
    maxLengthWarningSensitivity = 10,
    maxLengthClassName,
    maxLengthCentered,
    hasError,
    hasWarning,
    hasSuccess,
    clearable,
    collapse,
    active,
    type,
    pill,
    squared,
    onFocus = noop,
    onBlur = noop,
    onChange = noop,
    onValueChange = noop,
    onClear = noop,
    name,
    ...otherProps
  }: InputProps,
  forwardedRef: React.MutableRefObject<HTMLInputElement>
): React.ReactElement {
  const [_value, _setValue] = React.useState<string>(value);
  const [focused, setFocused] = React.useState(false);
  const [finalTabIndex, setTabIndex] = React.useState(tabIndex);
  const [ref] = React.useState<React.MutableRefObject<HTMLInputElement>>(forwardedRef || React.createRef());

  const focusInput = React.useCallback(() => {
    ref.current.focus();
  }, [ref]);

  React.useEffect(() => {
    if (autoFocus && ref?.current) focusInput();
  }, [autoFocus, ref, focusInput]);

  React.useEffect(() => {
    if (active && ref?.current) focusInput();
  }, [active, ref, focusInput]);

  React.useEffect(() => {
    _setValue(value);
  }, [value]);

  React.useEffect(() => {
    // force tabIndex to be -1 if component is in disabled state
    if (disabled) {
      setTabIndex(-1);
    } else {
      setTabIndex(tabIndex);
    }
  }, [disabled, tabIndex]);

  function handleClear(ev: React.MouseEvent<HTMLDivElement>) {
    ev.stopPropagation();
    const value = '';
    onValueChange(value);
    _setValue(value);
    onClear();
  }

  function handleChange(ev: React.ChangeEvent<HTMLInputElement>) {
    // don't do anything if disabled
    if (disabled) return;

    let value = ev.target?.value;
    // maxlength limit for type="number"
    if (type === 'number' && value.length > maxLength) {
      value = value.slice(0, maxLength);
      // set the value on the event as well to prevent any change
      ev.target.value = value;
    }

    onValueChange(value);
    onChange(ev);
    _setValue(value);
  }

  function handleFocus(ev: React.FocusEvent<HTMLInputElement>): void {
    // disabled components should not receive focus
    // note: onFocus should never be called in the first place
    // components in this state should have a tabIndex of -1
    if (disabled) return;

    setFocused(true);
    if (onFocus) onFocus(ev);
    if (selectOnFocus) {
      // autoselect after 300ms (user should have released their click / tap by then)
      const target = ev.target;
      setTimeout(() => target.select(), 300);
    }
  }

  function handleBlur(ev: React.FocusEvent<HTMLInputElement>): void {
    setFocused(false);
    if (onBlur) onBlur(ev);
  }

  const hasMaxLength = maxLength > 0 && maxLengthDisplay && focused;
  const charLength = _value?.length;
  const maxLengthError = hasMaxLength && charLength > maxLength;
  const maxLengthWarning =
    hasMaxLength &&
    !maxLengthError &&
    maxLengthWarningSensitivity &&
    charLength >= maxLength - maxLengthWarningSensitivity;

  const shouldShowMaxLength = focused && value?.length > 0;

  const hasValue = charLength > 0;
  const collapsable = collapse && !active;

  const blockClasses = classnames([
    `${inputBlockName}__wrapper`,
    { [`${inputBlockName}--${variant}`]: variant },
    { [`${inputBlockName}--size-${size}`]: size },
    { [`${inputBlockName}--disabled`]: disabled },
    { [`${inputBlockName}--focus`]: focused || focus },
    { [`${inputBlockName}--has-prepend`]: prepend },
    { [`${inputBlockName}--has-append`]: append },
    { [`${inputBlockName}--max-length-centered`]: maxLengthCentered },
    { [`${inputBlockName}--has-max-length`]: hasMaxLength },
    { [`${inputBlockName}--has-error`]: hasError || maxLengthError },
    { [`${inputBlockName}--has-warning`]: hasWarning || maxLengthWarning },
    { [`${inputBlockName}--max-length-visible`]: shouldShowMaxLength },
    { [`${inputBlockName}--has-success`]: hasSuccess },
    { [`${inputBlockName}--pill`]: pill },
    { [`${inputBlockName}--squared`]: squared },
    { [`${inputBlockName}--has-value`]: hasValue },
    { [`${inputBlockName}--collapsable`]: collapsable },

    className || '',
  ]);

  const maxLengthClasses = classnames([
    `${inputBlockName}__max-length`,
    { [`${inputBlockName}__max-length--warning`]: maxLengthWarning },
    maxLengthClassName || '',
  ]);

  const inputProps = {
    value: _value,
    disabled,
    type,
    maxLength,
    name,
    tabIndex: finalTabIndex,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onChange: handleChange,
    ...otherProps,
    className: `${inputBlockName}__input`,
  };

  let InputComponent: React.ReactElement;
  if (customInput) {
    // allow a custom component to be provided that will act as the input field
    // don't pass refs to custom input because we are not sure if they support it
    delete inputProps['ref'];
    InputComponent = customInput(inputProps);
  } else {
    InputComponent = <input {...inputProps} ref={ref} />;
  }

  // hidden input fields should not render a wrapper
  if (type === 'hidden') return InputComponent;

  const prependElements = Array.isArray(prepend) ? prepend : [prepend];
  const prependBlocks = (
    <div className={`${inputBlockName}__prepend-wrapper`}>
      {prependElements.map((item, index) => {
        return (
          <div key={`prepend-${index}`} className={`${inputBlockName}__prepend ${prependClasses}`}>
            {item}
          </div>
        );
      })}
    </div>
  );

  const appendElements = Array.isArray(append) ? append : [append];
  const appendBlocks = (
    <div className={`${inputBlockName}__append-wrapper`}>
      {appendElements.map((item, index) => {
        return (
          <div key={`prepend-${index}`} className={`${inputBlockName}__append ${appendClasses}`}>
            {item}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={blockClasses}>
      <div className={`${inputBlockName}__input-wrapper`}>
        {prepend && prependBlocks}
        {InputComponent}
        {append && appendBlocks}
        {clearable && (!collapse || active) && (
          <div className={`${inputBlockName}__clear`} onClick={handleClear}>
            <SvgIcon size="sm" name="X" />
          </div>
        )}
      </div>
      {hasMaxLength && (
        <div className={maxLengthClasses}>
          {charLength}/{maxLength}
        </div>
      )}
    </div>
  );
}

export default React.forwardRef<HTMLInputElement, InputProps>(Input);

// Also export the component without a forwardRef.
// Used in storybook to extract component properties.
export const InputNoRef = Input;
